import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Check, Crown, Zap, Heart, Loader2, Sparkles, SlidersHorizontal, Star, Rocket } from 'lucide-react';
import UpgradeCelebrationModal from '@/components/UpgradeCelebrationModal';

// Feb 2026 — contextual paywall messages. When a feature limit is hit,
// callers navigate here with `?feature=<name>&reason=<code>` and this map
// renders a headline banner explaining why they landed. Any unknown code
// falls back to the generic membership pitch.
const PAYWALL_CONTEXT = {
  // Undo (rewind)
  undo_locked:  { icon: Sparkles, headline: 'Rewind is a Premium feature',
                  sub: 'Un-swipe up to 3 profiles a day with Premium. VIPs get unlimited rewinds.',
                  highlight: 'premium' },
  undo_cap_hit: { icon: Sparkles, headline: 'You\u2019ve used today\u2019s 3 rewinds',
                  sub: 'Upgrade to VIP for unlimited undo swipes. Resets at midnight UTC.',
                  highlight: 'vip' },
  // Super Like (Serious Interest)
  super_like_locked:  { icon: Star, headline: 'Super Likes are Premium & VIP',
                        sub: 'Premium: 5/week. VIP: 10/week. Cut through the deck and get seen first.',
                        highlight: 'premium' },
  super_like_cap_hit: { icon: Star, headline: 'You\u2019ve used all your Super Likes',
                        sub: 'VIP gets 10/week vs Premium\u2019s 5. Or buy Serious Interests one-off in the Store.',
                        highlight: 'vip' },
  // Boost (Meet Priority)
  boost_locked:  { icon: Rocket, headline: 'Meet Priority is a Premium feature',
                   sub: 'Premium: 1 boost/week. VIP: 3 boosts/week. Skip to the top of the deck for 60 minutes.',
                   highlight: 'premium' },
  boost_cap_hit: { icon: Rocket, headline: 'You\u2019ve used this week\u2019s boost',
                   sub: 'VIP gets 3 boosts/week vs Premium\u2019s 1. Or buy more in the Store.',
                   highlight: 'vip' },
  // Filter gates
  filter_premium_locked: { icon: SlidersHorizontal, headline: 'Premium filters unlock the deck',
                           sub: 'Filter by education, height, industry, cannabis, marital history and 15+ more.',
                           highlight: 'premium' },
  filter_vip_locked:     { icon: SlidersHorizontal, headline: 'VIP unlocks compatibility filters',
                           sub: 'Match on Love Language, Communication Style, Family Plans and Social Energy.',
                           highlight: 'vip' },
};

export default function Subscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paywallCtx = useMemo(() => {
    const reason = searchParams.get('reason');
    return reason ? PAYWALL_CONTEXT[reason] || null : null;
  }, [searchParams]);
  const [tiers, setTiers] = useState({});
  const [currentTier, setCurrentTier] = useState('free');
  const [currentBilling, setCurrentBilling] = useState('monthly');
  const [billing, setBilling] = useState('yearly');
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [polling, setPolling] = useState(false);
  // Feb 27 2026 · Phase 165 — General upgrade celebration modal.
  // Fires after a successful Stripe checkout returns paid. `celebrateTier`
  // is set to 'premium' | 'vip' while the modal is open.
  const [celebrateTier, setCelebrateTier] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/subscriptions/tiers').then(r => setTiers(r.data.tiers)),
      api.get('/subscriptions/my').then(r => {
        setCurrentTier(r.data.subscription?.tier || 'free');
        setCurrentBilling(r.data.subscription?.billing || 'monthly');
      })
    ]).catch(() => {}).finally(() => setLoading(false));

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      setPolling(true);
      pollPaymentStatus(sessionId, 0);
      window.history.replaceState(null, '', window.location.pathname);
    }
    // Feb 2026 — when landing here with `?reason=<paywall_code>` (e.g.
    // undo_locked), auto-preselect the plan tier called out by the context.
    // The user should be able to hit Subscribe without extra tapping.
    const reason = params.get('reason');
    if (reason && PAYWALL_CONTEXT[reason]?.highlight === 'vip') {
      setBilling('yearly');
    }
    // Feb 2026 — Track paywall view for conversion analytics. Attribution
    // to a specific `reason=` code lets admin analytics answer "which lock
    // sells best" so we can tune the copy.
    if (reason) {
      api.post('/analytics/paywall-view', {
        reason,
        feature: params.get('feature') || 'unknown',
      }).catch(() => {});
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempt) => {
    if (attempt >= 5) { setPolling(false); toast.error('Payment status check timed out'); return; }
    try {
      const r = await api.get(`/payments/status/${sessionId}`);
      if (r.data.status === 'paid') {
        setCurrentTier(r.data.tier);
        setCurrentBilling(r.data.billing || 'monthly');
        // Feb 27 2026 · Phase 165 — Replace the plain toast with a full
        // celebration modal for paid tiers. Free downgrade path (subscribe
        // handler below) still uses a toast because there's nothing to
        // celebrate. Also stamp the filter-panel dedupe key so users don't
        // double-celebrate when they open Discovery Filters right after.
        if (r.data.tier === 'premium' || r.data.tier === 'vip') {
          setCelebrateTier(r.data.tier);
          try { localStorage.setItem('mtk_last_seen_filter_tier', r.data.tier); } catch { /* private mode */ }
        } else {
          toast.success(`Upgraded to ${r.data.tier}!`);
        }
        setPolling(false);
        return;
      }
    } catch {}
    setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
  };

  const subscribe = async (tier) => {
    if (tier === 'free') {
      setSubscribing(tier);
      try {
        await api.post('/subscriptions/subscribe', { tier });
        setCurrentTier(tier);
        toast.success('Downgraded to Free');
      } catch { toast.error('Failed'); }
      finally { setSubscribing(null); }
      return;
    }
    setSubscribing(tier);
    try {
      const r = await api.post('/payments/checkout', { tier, billing, origin_url: window.location.origin });
      if (r.data.url) {
        window.location.href = r.data.url;
      }
    } catch (err) {
      // SEC-001 fix (Feb 2026 audit): the demo-mode 503 fallback that
      // silently self-granted the tier is REMOVED. Paid tiers must go
      // through Stripe. If Stripe is misconfigured, surface a clear
      // error so ops can fix it — do NOT bypass the paywall client-side.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 503) {
        toast.error('Payments are temporarily unavailable. Please try again in a moment.');
      } else {
        toast.error(detail || 'Payment failed');
      }
    } finally { setSubscribing(null); }
  };

  if (loading) return <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" /></div>;

  const tierOrder = ['free', 'premium', 'vip'];
  const tierIcons = { free: Heart, premium: Zap, vip: Crown };
  const tierColors = { free: '#8A8A93', premium: '#B91C1C', vip: '#B38138' };

  // Feb 2026 — expanded from 2 billing periods to 5. Each row drives one
  // pill in the billing toggle + reads the matching price field on the
  // tier and renders the "/yr, /mo, /wk" suffix accordingly.
  const BILLING_OPTIONS = [
    { key: 'weekly',     label: '1 week',   priceField: 'price_weekly',     suffix: '/wk',  savePct: null,
      months: 0.25, badge: null },
    { key: 'monthly',    label: 'Monthly',  priceField: 'price_monthly',    suffix: '/mo',  savePct: null,
      months: 1, badge: null },
    { key: 'quarterly',  label: '3 months', priceField: 'price_quarterly',  suffix: '/3mo', savePct: null,
      months: 3, badge: 'Popular' },
    { key: 'semiannual', label: '6 months', priceField: 'price_semiannual', suffix: '/6mo', savePct: null,
      months: 6, badge: 'Best value' },
    { key: 'yearly',     label: 'Yearly',   priceField: 'price_yearly',     suffix: '/yr',  savePct: null,
      months: 12, badge: 'Save 30%' },
  ];
  const currentBillingOpt = BILLING_OPTIONS.find(b => b.key === billing) || BILLING_OPTIONS[1];

  const priceFor = (tier, key) => {
    if (!tier || key === 'free') return 0;
    return tier[currentBillingOpt.priceField] ?? tier.price_monthly ?? 0;
  };
  // Equivalent /month price for anything > 1 month billing periods, so users
  // can compare like-for-like. Weekly is < 1 month so we skip.
  const monthlyEquivalent = (tier) => {
    const p = priceFor(tier, 'premium');   // 'premium' is a non-free sentinel
    if (currentBillingOpt.months <= 1) return null;
    return p / currentBillingOpt.months;
  };

  return (
    <div className="min-h-screen bg-[var(--cream)] px-6 py-8 pb-28" data-testid="subscription-page">
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#4B4B52] hover:text-[#0F0F10] mb-8 text-sm" data-testid="sub-back">
          <ArrowLeft size={16}/> Back
        </button>

        <p className="text-[11px] tracking-[0.2em] uppercase text-[#B91C1C] font-semibold mb-2">Membership</p>
        <h1 className="font-display-tight text-[26px] sm:text-3xl font-[600] tracking-tight mb-3 leading-[1.15] whitespace-nowrap">Choose your <em className="italic font-[500] brand-gradient-text">plan</em></h1>
        <p className="text-[#4B4B52] text-sm mb-8">Upgrade when you're ready. Cancel anytime.</p>

        {/* Feb 2026 — contextual paywall banner. Shows when the user
            arrived here after hitting a specific feature limit (undo,
            filter, etc.). Renders nothing on a plain /subscription visit. */}
        {paywallCtx && (
          <div
            data-testid={`paywall-context-${searchParams.get('reason')}`}
            className="flex items-start gap-3 rounded-2xl border border-[#B91C1C]/20 bg-[#FEF6F1] p-4 mb-6"
          >
            <div className="mt-0.5 w-8 h-8 rounded-full brand-gradient flex items-center justify-center text-white shrink-0">
              {(() => { const Icon = paywallCtx.icon || Sparkles; return <Icon size={16} strokeWidth={2} />; })()}
            </div>
            <div className="flex-1">
              <p className="text-[14px] font-bold text-[#0F0F10] leading-snug">{paywallCtx.headline}</p>
              <p className="text-[12px] text-[#4B4B52] mt-0.5 leading-snug">{paywallCtx.sub}</p>
            </div>
          </div>
        )}

        {/* Billing toggle — 5 periods (Feb 2026). Horizontally scrollable
            on narrow viewports; wraps on wider screens. */}
        <div
          className="flex items-center gap-1 p-1 rounded-full bg-black/[0.04] border border-[var(--hairline)] mb-8 overflow-x-auto no-scrollbar"
          data-testid="billing-toggle"
        >
          {BILLING_OPTIONS.map(opt => {
            const active = billing === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setBilling(opt.key)}
                data-testid={`billing-${opt.key}`}
                className={`shrink-0 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-all inline-flex items-center gap-2 ${
                  active ? 'bg-white shadow-sm text-[#0F0F10]' : 'text-[#4B4B52] hover:text-[#0F0F10]'
                }`}
              >
                {opt.label}
                {opt.badge && (
                  <span className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(179,129,56,0.12)', color: '#B38138' }}>
                    {opt.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="space-y-4">
          {tierOrder.map(key => {
            const tier = tiers[key];
            if (!tier) return null;
            const Icon = tierIcons[key];
            const color = tierColors[key];
            const isCurrent = currentTier === key && (key === 'free' || currentBilling === billing);
            const isPopular = key === 'premium';
            const price = priceFor(tier, key);
            const perMonth = key !== 'free' && currentBillingOpt.months > 1
              ? (price / currentBillingOpt.months).toFixed(2) : null;

            return (
              <div key={key}
                className={`relative rounded-2xl p-6 transition-all premium-card ${isPopular ? 'elevated-card border-2 border-[#B91C1C]/25' : ''} ${isCurrent ? 'ring-2 ring-[#B91C1C]/30' : ''}`}
                data-testid={`tier-${key}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 right-6 px-3 py-1 brand-gradient rounded-full text-[10px] font-bold uppercase tracking-[0.12em] text-white">
                    Most Popular
                  </div>
                )}

                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{background: `${color}12`, border:`1px solid ${color}28`}}>
                      <Icon size={20} style={{color}} strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="font-display font-[600] text-xl tracking-tight">{tier.name}</h3>
                      <div className="flex items-baseline gap-1 mt-0.5">
                        {price === 0 ? (
                          <span className="text-[#8A8A93] text-sm">Free forever</span>
                        ) : (
                          <>
                            <span className="font-display-tight text-2xl font-[600] text-[#0F0F10]">${price.toFixed(2)}</span>
                            <span className="text-xs text-[#8A8A93]">{currentBillingOpt.suffix}</span>
                          </>
                        )}
                      </div>
                      {perMonth && (
                        <p className="text-[11px] text-[#B38138] mt-0.5 font-medium">${perMonth}/mo equivalent</p>
                      )}
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="text-[10px] font-bold tracking-wider uppercase text-[#B91C1C] bg-[#B91C1C]/08 px-2.5 py-1 rounded-full border border-[#B91C1C]/15">Current</span>
                  )}
                </div>

                <ul className="space-y-2.5 mb-5">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-[#4B4B52]">
                      <Check size={14} style={{color}} className="flex-shrink-0" strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>

                {!isCurrent && (
                  <button onClick={() => subscribe(key)} disabled={!!subscribing || polling}
                    data-testid={`subscribe-${key}`}
                    className={`btn-shine w-full py-3.5 rounded-full font-semibold text-sm transition-all active:scale-[0.98] ${
                      key === 'vip' ? 'text-white' :
                      key === 'premium' ? 'brand-gradient text-white brand-glow-sm hover:scale-[1.01]' :
                      'border border-[rgba(15,15,16,0.12)] hover:bg-black/[0.02] text-[#0F0F10]'
                    }`}
                    style={key === 'vip' ? {background:'linear-gradient(135deg, #B38138 0%, #8A5E24 100%)', boxShadow:'0 6px 18px -4px rgba(179,129,56,0.35)'} : undefined}
                  >
                    {subscribing === key ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> :
                      key === 'free' ? 'Downgrade to Free' : `Get ${tier.name} · ${currentBillingOpt.label}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-[#8A8A93] text-xs mt-8">
          Subscriptions renew automatically. Cancel anytime.<br />
          {currentBillingOpt.months > 1 && `${currentBillingOpt.label} plans lock in a lower monthly rate.`}
          <br />
          <span className="text-[#B38138] font-medium">Have a promo code? Apply at checkout.</span>
        </p>
      </div>
      {celebrateTier && (
        <UpgradeCelebrationModal tier={celebrateTier} onClose={() => setCelebrateTier(null)} />
      )}
    </div>
  );
}
